'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  Bookmark,
  Folder,
  UserBookmarks,
  WorkEngagementBookmarks,
} from '@/lib/bookmark/types';
import { useSessionStorage, useLocalStorage } from 'usehooks-ts';
import { useWorkflowsData } from '@/app/dashboard/workflows/hooks/use-workflows-data';
import type { OrganizationUserWorkEngagement } from '@pigello/pigello-matrix';

type AccordionType = 'dashboard' | 'workflows';

export type WorkflowSettings = {
  expandedItems: Record<string, string[]>;
};

interface EditingItemState {
  item: Folder | Bookmark;
  type: 'folder' | 'bookmark';
  workEngagementId: string | null;
}

interface DeletingItemState {
  item: Folder | Bookmark;
  type: 'folder' | 'bookmark';
  workEngagementId: string | null;
}

interface SharingItemState {
  item: Folder | Bookmark;
  type: 'folder' | 'bookmark';
  workEngagementId: string | null;
}

interface WorkflowDataContextValue {
  readonly userWorkflows: UserBookmarks | undefined;
  readonly isLoadingUserWorkflows: boolean;
  readonly workEngagements:
    | { list: OrganizationUserWorkEngagement[] | undefined }
    | undefined;
  readonly workEngagementWorkflows: WorkEngagementBookmarks[] | undefined;
  readonly isLoadingWorkEngagementWorkflows: boolean;
  readonly sharedFolders: Folder[] | undefined;
  readonly workflows: UserBookmarks | WorkEngagementBookmarks | undefined;
  readonly setWorkflows: (
    workflows: UserBookmarks | WorkEngagementBookmarks
  ) => void;
  readonly save: (
    data: UserBookmarks | WorkEngagementBookmarks
  ) => Promise<unknown>;
  readonly isSaving: boolean;
  /** True when this provider is scoped to work engagement bookmarks (settings / role workflows). */
  readonly isWorkEngagementBookmarksContext: boolean;
}

interface WorkflowContextValue extends WorkflowDataContextValue {
  readonly accordionType: AccordionType;
  readonly isDashboard: boolean;
  readonly setIsDashboard: (isDashboard: boolean) => void;
  readonly currentWorkflowFolder: Folder | null;
  readonly setCurrentWorkflowFolder: (folder: Folder | null) => void;
  readonly currentWorkflowBookmark: Bookmark | null;
  readonly setCurrentWorkflowBookmark: (bookmark: Bookmark | null) => void;
  readonly workflowSettings: WorkflowSettings;
  readonly setWorkflowSettings: (workflowSettings: WorkflowSettings) => void;
  readonly toggleAccordion: (accordionId: string, isOpen: boolean) => void;

  readonly isActionModalOpen: boolean;
  readonly setIsActionModalOpen: (isOpen: boolean) => void;

  readonly modals: {
    readonly edit: {
      readonly state: EditingItemState | null;
      readonly open: (
        item: Folder | Bookmark,
        type: 'folder' | 'bookmark',
        workEngagementId?: string | null
      ) => void;
      readonly close: () => void;
    };
    readonly delete: {
      readonly state: DeletingItemState | null;
      readonly open: (
        item: Folder | Bookmark,
        type: 'folder' | 'bookmark',
        workEngagementId?: string | null
      ) => void;
      readonly close: () => void;
    };
    readonly share: {
      readonly state: SharingItemState | null;
      readonly open: (
        item: Folder | Bookmark,
        type: 'folder' | 'bookmark',
        workEngagementId?: string | null
      ) => void;
      readonly close: () => void;
    };
  };
}

interface WorkflowProviderProps {
  readonly children: React.ReactNode;
  readonly workEngagementIds?: string[];
}

const WorkflowContext = createContext<WorkflowContextValue | null>(null);

export const WorkflowProvider = ({
  children,
  workEngagementIds,
}: WorkflowProviderProps) => {
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);

  // Modal states
  const [editingItem, setEditingItem] = useState<EditingItemState | null>(null);
  const [deletingItem, setDeletingItem] = useState<DeletingItemState | null>(
    null
  );
  const [sharingItem, setSharingItem] = useState<SharingItemState | null>(null);

  // 1. Data & Persistence
  const {
    workflows,
    setWorkflows,
    userWorkflows,
    isLoadingUserWorkflows,
    workEngagements,
    workEngagementWorkflows,
    isLoadingWorkEngagementWorkflows,
    save,
    isSaving,
    sharedFolders,
    isWorkEngagementBookmarksContext,
  } = useWorkflowsData({ workEngagementIds });

  const [isDashboard, setIsDashboard] = useState<boolean>(false);
  const [currentWorkflowFolder, setCurrentWorkflowFolderSession] =
    useSessionStorage<Folder | null>('active_workflow_folder', null);
  const [currentWorkflowBookmark, setCurrentWorkflowBookmarkSession] =
    useSessionStorage<Bookmark | null>('active_workflow_bookmark', null);
  const [workflowSettings, setWorkflowSettings] =
    useLocalStorage<WorkflowSettings>('workflowSettings', {
      expandedItems: {},
    });

  const accordionType = useMemo<AccordionType>(
    () => (isDashboard ? 'dashboard' : 'workflows'),
    [isDashboard]
  );

  const setCurrentWorkflowFolder = useCallback(
    (folder: Folder | null) => {
      setCurrentWorkflowFolderSession(folder);
    },
    [setCurrentWorkflowFolderSession]
  );

  const setCurrentWorkflowBookmark = useCallback(
    (bookmark: Bookmark | null) => {
      setCurrentWorkflowBookmarkSession(bookmark);
    },
    [setCurrentWorkflowBookmarkSession]
  );

  const toggleAccordion = useCallback(
    (accordionId: string, isOpen: boolean) => {
      setWorkflowSettings((prev) => {
        const current = prev.expandedItems[accordionType] ?? [];

        const next = isOpen
          ? current.includes(accordionId)
            ? current
            : [...current, accordionId]
          : current.filter((id) => id !== accordionId);

        return {
          ...prev,
          expandedItems: {
            ...prev.expandedItems,
            [accordionType]: next,
          },
        };
      });
    },
    [accordionType, setWorkflowSettings]
  );

  // Edit
  const openEdit = useCallback(
    (
      item: Folder | Bookmark,
      type: 'folder' | 'bookmark',
      workEngagementId: string | null = null
    ) => {
      setEditingItem({ item, type, workEngagementId });
      setIsActionModalOpen(true);
    },
    []
  );

  const closeEdit = useCallback(() => {
    setEditingItem(null);
    setIsActionModalOpen(false);
  }, []);

  // Delete
  const openDelete = useCallback(
    (
      item: Folder | Bookmark,
      type: 'folder' | 'bookmark',
      workEngagementId: string | null = null
    ) => {
      setDeletingItem({ item, type, workEngagementId });
      setIsActionModalOpen(true);
    },
    []
  );

  const closeDelete = useCallback(() => {
    setDeletingItem(null);
    setIsActionModalOpen(false);
  }, []);

  // Share
  const openShare = useCallback(
    (
      item: Folder | Bookmark,
      type: 'folder' | 'bookmark',
      workEngagementId: string | null = null
    ) => {
      setSharingItem({ item, type, workEngagementId });
      setIsActionModalOpen(true);
    },
    []
  );

  const closeShare = useCallback(() => {
    setSharingItem(null);
    setIsActionModalOpen(false);
  }, []);

  const modals = useMemo(
    () => ({
      edit: { state: editingItem, open: openEdit, close: closeEdit },
      delete: { state: deletingItem, open: openDelete, close: closeDelete },
      share: { state: sharingItem, open: openShare, close: closeShare },
    }),
    [
      openEdit,
      openDelete,
      openShare,
      closeEdit,
      closeDelete,
      closeShare,
      editingItem,
      deletingItem,
      sharingItem,
    ]
  );

  const value = useMemo(
    () => ({
      // Workflow data
      userWorkflows,
      isLoadingUserWorkflows,
      workEngagements,
      workEngagementWorkflows,
      isLoadingWorkEngagementWorkflows,
      sharedFolders,
      workflows,
      setWorkflows,
      save,
      isSaving,
      isWorkEngagementBookmarksContext,
      // UI state
      accordionType,
      isDashboard,
      setIsDashboard,
      currentWorkflowFolder,
      setCurrentWorkflowFolder,
      currentWorkflowBookmark,
      setCurrentWorkflowBookmark,
      workflowSettings,
      setWorkflowSettings,
      toggleAccordion,
      isActionModalOpen,
      setIsActionModalOpen,
      modals,
    }),
    [
      // Workflow data
      userWorkflows,
      isLoadingUserWorkflows,
      workEngagements,
      workEngagementWorkflows,
      isLoadingWorkEngagementWorkflows,
      sharedFolders,
      workflows,
      setWorkflows,
      save,
      isSaving,
      isWorkEngagementBookmarksContext,
      // UI state
      accordionType,
      isDashboard,
      setIsDashboard,
      currentWorkflowFolder,
      setCurrentWorkflowFolder,
      currentWorkflowBookmark,
      setCurrentWorkflowBookmark,
      workflowSettings,
      setWorkflowSettings,
      toggleAccordion,
      isActionModalOpen,
      setIsActionModalOpen,
      modals,
    ]
  );

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflows = (): WorkflowContextValue => {
  const workflowContext = useContext(WorkflowContext);
  if (!workflowContext) {
    throw new Error('useWorkflows must be used within a WorkflowProvider');
  }

  return workflowContext;
};
